import { RunnerOwnerKind, runnerOwnershipValid } from '@developerz/domain';
import { createEffect, createSignal, type JSX, on, Show } from 'solid-js';
import { t } from '../i18n';
import { type BoxOwnership, boxOwnerCall } from '../lib/box-lifecycle';
import { OWNER_KIND_DESC, OWNER_KIND_LABEL } from '../lib/box-owner-kind';
import type { OrgMemberRow } from '../lib/org-members';
import { CopyAsCall } from './CopyAsCall';
import { Field } from './Field';
import { Select, type SelectOption } from './Select';
import { buttonClass } from './ui/Button';

// Retarget which work a box may claim (`PUT /v1/runners/:id/owner`). Presenter —
// the dialog owns the write and hands down the API's refusal.
//
// THE OWNER IS PICKED, NEVER TYPED. The API accepts exactly two references: an
// `org` box may point only at THIS account (a foreign `accounts.id` would aim a
// box at another tenant's queue, which is why it is refused), and a `user` box
// only at a member of it. Both are things the console already knows, so the form
// offers them — a raw uuid box would invite a 422 the reader cannot debug and
// would ask a human to look up an id that is on screen a moment later.
//
// The pairing rule is the API's and stays the API's: BOTH kinds must name an
// owner, so the save is gated on `runnerOwnershipValid` — the domain's own shape
// predicate, which both write doors spend — rather than on a second spelling of
// it here. The form renders the field that pair needs and disables a save it
// cannot address; the 422 still renders here verbatim, because the membership
// check can only be answered by the server.
//
// The picker's vocabulary is `RunnerOwnerKind` itself, and that is what made
// retiring `shared` a defect fix rather than a rename: the write seams refused
// that value, and this select went on offering it — an ownership an operator
// could pick and the API could only refuse.

export function BoxOwnerForm(props: {
  /** The box being retargeted — the address the copied calls carry. */
  readonly boxId: string;
  readonly ownerKind: RunnerOwnerKind;
  readonly ownerId: string | null;
  /** The one `accounts.id` an `org` box may name — the active account's. */
  readonly orgOwnerId: string | null;
  /** How that account reads on screen (its GitHub login). */
  readonly orgLabel: string;
  readonly members: readonly OrgMemberRow[];
  readonly pending: boolean;
  readonly error: string | null;
  readonly onSave: (ownership: BoxOwnership) => void;
}): JSX.Element {
  const [kind, setKind] = createSignal<RunnerOwnerKind>(props.ownerKind);
  const [userId, setUserId] = createSignal('');

  // Seeding local field state from the point read is a legitimate effect: it
  // initializes when the dialog re-targets, it does not mirror derived state.
  createEffect(
    on(
      () => [props.ownerKind, props.ownerId] as const,
      ([ownerKind, ownerId]) => {
        setKind(ownerKind);
        setUserId(ownerKind === RunnerOwnerKind.User ? (ownerId ?? '') : '');
      },
    ),
  );

  /** The stored owner is a member who has since left — keep it selectable, labelled. */
  const staleOwner = (): string | null => {
    const stored = userId();
    return stored !== '' && !props.members.some((m) => m.user_id === stored) ? stored : null;
  };

  /** The blank placeholder first, then the member who has since left (kept
   *  selectable, labelled), then every current member. */
  const memberOptions = (): SelectOption<string>[] => {
    const stale = staleOwner();
    return [
      { value: '', label: t('fleet.panel.owner_member_placeholder') },
      ...(stale === null
        ? []
        : [{ value: stale, label: t('fleet.panel.owner_member_gone', { id: stale }) }]),
      ...props.members.map((member) => ({ value: member.user_id, label: member.login })),
    ];
  };

  const ownerId = (): string | null => {
    if (kind() === RunnerOwnerKind.Org) return props.orgOwnerId;
    return userId() === '' ? null : userId();
  };
  const valid = (): boolean => runnerOwnershipValid({ ownerKind: kind(), ownerId: ownerId() });

  const submit = (e: Event): void => {
    e.preventDefault();
    if (!valid() || props.pending) return;
    props.onSave({ owner_kind: kind(), owner_id: ownerId() });
  };

  return (
    <form class="field-group box-manage__form" onSubmit={submit}>
      <Field
        label={t('fleet.panel.owner_label')}
        id="box-owner-kind"
        desc={t(OWNER_KIND_DESC[kind()])}
      >
        {(a11y) => (
          <Select
            a11y={a11y}
            label={t('fleet.panel.owner_label')}
            block
            value={kind()}
            options={Object.values(RunnerOwnerKind).map((value) => ({
              value,
              label: t(OWNER_KIND_LABEL[value]),
            }))}
            onChange={setKind}
          />
        )}
      </Field>

      <Show when={kind() === RunnerOwnerKind.Org}>
        <p class="field__desc">
          {props.orgOwnerId === null
            ? t('fleet.panel.owner_org_unknown')
            : t('fleet.panel.owner_org_value', { org: props.orgLabel })}
        </p>
      </Show>

      <Show when={kind() === RunnerOwnerKind.User}>
        <Field
          label={t('fleet.panel.owner_member_label')}
          id="box-owner-user"
          desc={props.members.length === 0 ? t('fleet.panel.owner_member_empty') : undefined}
        >
          {(a11y) => (
            <Select
              a11y={a11y}
              label={t('fleet.panel.owner_member_label')}
              block
              value={userId()}
              options={memberOptions()}
              onChange={setUserId}
            />
          )}
        </Field>
      </Show>

      <Show when={props.error}>
        {(msg) => (
          <p class="field__error" role="alert">
            {msg()}
          </p>
        )}
      </Show>

      <div class="box-manage__actions">
        <button
          type="submit"
          class={buttonClass({ size: 'sm' })}
          disabled={!valid() || props.pending}
        >
          {t(props.pending ? 'fleet.panel.saving' : 'fleet.panel.owner_save')}
        </button>
      </div>

      {/* The DRAFT, not the stored pair: the copied call is the one Save would
          make right now, which is what makes it a usable equivalent. */}
      <CopyAsCall
        calls={[
          {
            label: t('fleet.panel.owner_save'),
            call: boxOwnerCall(props.boxId, { owner_kind: kind(), owner_id: ownerId() }),
          },
        ]}
      />
    </form>
  );
}
